import React, { useState, useRef, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  InputAdornment,
  AutocompleteInputChangeReason,
} from "@mui/material";
import Label from "../../../../../common/components/Label";
import CLOSE_ICON from "../../../../../constants/icons/CloseIcon";
import { useGetJobTitleSuggestionsMutation } from "../../../../../redux/services/campaignSetupService";
import StackWrapper from "../../StackWrapper";
import { getRandomValue } from "../../../../../utils/whatsAppUtils";

export interface Option {
  value: string;
  label: string;
}

interface IProps {
  jobTitle: string;
  handleUpdateJobTitle: (newJobTitle: string) => void;
  error?: string;
}

const JobTitleWrapper: React.FC<IProps> = ({
  jobTitle,
  handleUpdateJobTitle,
  error,
}) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [options, setOptions] = useState<Option[]>([]);
  const [focused, setFocused] = useState(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [
    triggerGetJobList,
    { data: jobListSuggestions, isLoading, isSuccess },
  ] = useGetJobTitleSuggestionsMutation();

  const handleInputChange = (
    event: React.SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    setInputValue(value);

    // make api call only when input changes by typing, not by selecting option
    if (reason === "input") {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // don't make api when input is cleared
      if (value) {
        debounceTimeoutRef.current = setTimeout(() => {
          const random = getRandomValue();
          triggerGetJobList({ jobTitle: value, random });
        }, 500);
      }
    }

    // clear option when input is empty
    if (!value) {
      setOptions([]);
    }
  };

  const handleSelectionChange = (
    event: React.SyntheticEvent,
    value: string | Option | null
  ) => {
    handleUpdateJobTitle((value as Option)?.value || "");
  };

  const handleBlur = () => {
    if (jobTitle !== inputValue) {
      handleUpdateJobTitle(inputValue);
    }
  };

  const handleMouseEnter = () => {
    setFocused(true);
  };
  const handleMouseOut = () => {
    setFocused(false);
  };

  useEffect(() => {
    if (jobListSuggestions && isSuccess) {
      setOptions(jobListSuggestions);
    }
  }, [isSuccess, jobListSuggestions]);

  return (
    <StackWrapper>
      <Label colorType="light" label="Job title" required labelFor="jobTitle" />

      <Autocomplete
        value={jobTitle}
        onChange={handleSelectionChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onBlur={handleBlur}
        options={options}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.label
        }
        loading={isLoading}
        disableClearable
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Write job role here..."
            variant="outlined"
            fullWidth
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseOut}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress
                      sx={{ mr: 2 }}
                      color="inherit"
                      size={20}
                    />
                  ) : null}

                  {params.InputProps.endAdornment}

                  {!isLoading && !!inputValue && focused && (
                    <InputAdornment
                      position="end"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mr: 2,
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setInputValue("");
                        handleUpdateJobTitle("");
                        setOptions([]);
                      }}
                    >
                      <CLOSE_ICON />
                    </InputAdornment>
                  )}
                </>
              ),
            }}
            error={!!error && !jobTitle}
            helperText={!!error && !jobTitle ? error : ""}
          />
        )}
        isOptionEqualToValue={(option, value) => option.label === value.label}
        freeSolo
      />
    </StackWrapper>
  );
};

export default React.memo(JobTitleWrapper);
